import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchGuideMediaFile } from '../../../../../api/stay-guide-public-client';
import { GuideOpenBody, GuidePhoto } from '../../../../../interfaces/stay-guide';
import { AppButton } from '../../../../button/app-button';
import { ImageViewer } from '../../../../gallery/image-viewer';
import { Presence } from '../../../../../motion';
import { t } from '../../../../onboarding/stay/stay-strings';
import { toast } from '../../../../../utils/toast';
import { GuideH1, useGuideNav } from './guide-frame';
import { Mosaic, MosaicLead, MosaicTile, Muted, ShareReason, ShareSlot } from './guide.styled';

const L = 'en' as const;

/**
 * PHOTOS (plan S7.7): a mosaic (the first photo wide, then two columns), each
 * tile opening the viewer (arrow keys, Escape, "Photo 2 of 7").
 *
 * SHARE. iOS only opens the share sheet for a File it already holds when the
 * tap happens — awaiting a download inside the tap loses the gesture. So the
 * photo shown in the viewer is fetched as soon as it is shown (the guest file
 * route, through the gate), and the tap only hands that File over:
 *
 *   navigator.canShare({files}) → navigator.share({files, title})
 *     AbortError (the guest closed the sheet) → nothing
 *   any other failure, or no file sharing → download it (a[download]) and
 *     toast "Photo saved to your device"
 *
 * The host preview never calls a guest route: no prefetch, and Share is
 * disabled with its reason ("Sharing works on the guest's page.").
 */

const EXT: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

export const photoFileName = (photo: GuidePhoto, index: number, type: string): string => {
  const base = (photo.title || '').replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

  return `${base || `photo-${index + 1}`}.${EXT[type] || 'jpg'}`;
};

export const tileLabel = (photo: GuidePhoto, index: number): string =>
  photo.title ? t(L, 'guide.openPhoto', { title: photo.title }) : t(L, 'guide.openPhotoN', { n: index + 1 });

const download = (file: File) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const isAbort = (e: unknown): boolean => !!e && typeof e === 'object' && (e as { name?: string }).name === 'AbortError';

type ShareNavigator = Navigator & {
  canShare?: (data: { files?: File[] }) => boolean;
  share?: (data: { files?: File[]; title?: string }) => Promise<void>;
};

export const GuidePhotos: React.FC<{ body: GuideOpenBody }> = ({ body }) => {
  const nav = useGuideNav();
  const photos = body.media.gallery;
  const [openAt, setOpenAt] = useState<number | null>(null);
  const pending = useRef(new Map<string, Promise<File | null>>());
  const ready = useRef(new Map<string, File>());
  const tokenRef = useRef(nav.token);
  tokenRef.current = nav.token;

  // A new answer (a refetch) may carry other photos: forget the old files.
  useEffect(() => {
    const p = pending.current;
    const r = ready.current;

    return () => {
      p.clear();
      r.clear();
    };
  }, [photos]);

  const prefetch = useCallback(
    (index: number) => {
      if (nav.preview) return;
      const photo = photos[index];
      if (!photo || pending.current.has(photo.id)) return;
      const job = fetchGuideMediaFile(tokenRef.current, photo.id)
        .then(blob => {
          const type = blob.type || 'image/jpeg';
          const file = new File([blob], photoFileName(photo, index, type), { type });
          ready.current.set(photo.id, file);

          return file;
        })
        .catch(() => {
          pending.current.delete(photo.id);

          return null;
        });
      pending.current.set(photo.id, job);
    },
    [nav.preview, photos]
  );

  const share = async (index: number) => {
    const photo = photos[index];
    if (!photo || nav.preview) return;
    let file = ready.current.get(photo.id) || null;
    if (!file) {
      prefetch(index);
      file = (await pending.current.get(photo.id)) || null;
    }
    if (!file) {
      toast.error(t(L, 'guide.photoNotSaved'));

      return;
    }
    const n = navigator as ShareNavigator;
    if (typeof n.canShare === 'function' && typeof n.share === 'function') {
      let shareable = false;
      try {
        shareable = n.canShare({ files: [file] });
      } catch {
        shareable = false;
      }
      if (shareable) {
        try {
          await n.share({ files: [file], title: photo.title || undefined });

          return;
        } catch (e) {
          if (isAbort(e)) return;
        }
      }
    }
    try {
      download(file);
      toast.success(t(L, 'guide.photoSaved'));
    } catch {
      toast.error(t(L, 'guide.photoNotSaved'));
    }
  };

  return (
    <>
      <GuideH1>
        {t(L, 'guide.photosTitle')}
      </GuideH1>
      {photos.length ? (
        <Mosaic>
          {photos.map((photo, i) => {
            const Tile = i === 0 ? MosaicLead : MosaicTile;

            return (
              <li key={photo.id}>
                <Tile type="button" aria-label={tileLabel(photo, i)} onClick={() => setOpenAt(i)}>
                  <img src={photo.url} alt="" loading={i > 2 ? 'lazy' : undefined} />
                </Tile>
              </li>
            );
          })}
        </Mosaic>
      ) : (
        <Muted role="status">{t(L, 'guide.photosEmpty')}</Muted>
      )}

      <Presence>
        {openAt !== null ? (
          <ImageViewer
            key="guide-photo-viewer"
            images={photos}
            startIndex={openAt}
            onClose={() => setOpenAt(null)}
            onIndexChange={prefetch}
            altOf={i => (photos[i] && photos[i].title) || ''}
            countLabel={(i, total) => t(L, 'guide.photoCount', { n: i + 1, total })}
            actions={i => (
              <ShareSlot>
                <AppButton
                  variant="secondary"
                  size="compact"
                  onClick={() => share(i)}
                  disabled={nav.preview}
                  aria-describedby={nav.preview ? 'guide-share-reason' : undefined}
                >
                  {t(L, 'guide.sharePhoto')}
                </AppButton>
                {nav.preview ? <ShareReason id="guide-share-reason">{t(L, 'guide.shareInPreview')}</ShareReason> : null}
              </ShareSlot>
            )}
          />
        ) : null}
      </Presence>
    </>
  );
};
